/**
 * Development Mode Indicator Component
 * @implements TS-012 - Dev Mode Indicator
 *
 * Shows a visual indicator in development mode with quick auth controls.
 * Only renders when import.meta.env.DEV is true.
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  Button,
  Collapse,
  IconButton,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuthContext } from '@/providers';
import { AUTH_TOKEN_KEY } from '@/hooks/useAuth';

/**
 * Test user configurations for quick switching
 */
const TEST_USERS = [
  {
    email: 'po@test.com',
    role: 'product_owner',
    label: 'Product Owner',
    color: 'primary' as const,
  },
  {
    email: 'admin@test.com',
    role: 'platform_admin',
    label: 'Admin',
    color: 'warning' as const,
  },
];

/**
 * DevModeIndicator - Development mode UI indicator
 *
 * Features:
 * - Only renders in development mode (import.meta.env.DEV)
 * - Fixed position in bottom-right corner
 * - Red/orange "DEV MODE" badge
 * - Expandable panel showing:
 *   - Current user email and roles
 *   - Quick switch buttons for test users
 *
 * @implements TS-012.1 - DevModeIndicator component
 *
 * @example
 * // In App.tsx
 * function App() {
 *   return (
 *     <>
 *       <RouterProvider router={router} />
 *       <DevModeIndicator />
 *     </>
 *   );
 * }
 */
export const DevModeIndicator = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuthContext();

  /**
   * Switch to a test user by calling the dev login endpoint
   * @implements TS-012.1 - Quick switch buttons for test users
   */
  const handleSwitchUser = useCallback(async (email: string, role: string) => {
    setIsSwitching(true);
    try {
      const response = await fetch('/auth/dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
        credentials: 'include',
      });

      if (response.ok) {
        // Parse response to get token and store it for WebSocket auth
        // @implements FS-003.5.2 - WebSocket Authentication
        const data = await response.json();
        if (data.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, data.token);
        }
        // Reload to refresh auth state
        window.location.reload();
      } else {
        console.error(
          '[DevModeIndicator] Failed to switch user:',
          response.statusText,
        );
      }
    } catch (error) {
      console.error('[DevModeIndicator] Error switching user:', error);
    } finally {
      setIsSwitching(false);
    }
  }, []);

  // Only render in development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  const handleToggleExpand = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsExpanded(false);
  };

  // Minimized state - just show a small FAB
  if (isMinimized) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
        }}
      >
        <Fab
          size="small"
          onClick={handleToggleExpand}
          sx={{
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            '&:hover': {
              bgcolor: 'warning.dark',
            },
          }}
        >
          <BugReportIcon fontSize="small" />
        </Fab>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: 320,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          overflow: 'hidden',
          border: 2,
          borderColor: 'warning.main',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            px: 2,
            py: 1,
            cursor: 'pointer',
          }}
          onClick={handleToggleExpand}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">
              DEV MODE
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              sx={{ color: 'inherit', mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </Box>
        </Box>

        {/* Expandable Content */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* Current User Info */}
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Current User
            </Typography>
            {isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : isAuthenticated && user ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  {user.email}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mt: 0.5 }}
                  flexWrap="wrap"
                >
                  {user.roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not authenticated
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Quick Switch Buttons */}
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Quick Switch
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {TEST_USERS.map((testUser) => (
                <Button
                  key={testUser.email}
                  variant="outlined"
                  size="small"
                  color={testUser.color}
                  disabled={isSwitching || user?.email === testUser.email}
                  onClick={() =>
                    handleSwitchUser(testUser.email, testUser.role)
                  }
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {testUser.label}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 'auto' }}
                  >
                    {testUser.email}
                  </Typography>
                </Button>
              ))}
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
            >
              Uses /auth/dev endpoint
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};
